// Detecting "your tab is running a build that no longer exists on the server".
//
// The frontend is a code-split SPA on S3 + CloudFront, and `deploy-production.sh` syncs with
// --delete. A user who had the app open across a deploy keeps running the JS already in their
// browser, which is fine — until they open something whose chunk had not loaded yet. The
// browser then requests an old hashed filename that the sync has removed, and gets a 404.
//
// There are two paths that fails down, and only one of them can reach a React error boundary:
//   * React.lazy() components — the failure happens during render, so a boundary catches it.
//   * plain `await import(...)` at runtime (plotly, jsPDF, duckdb-wasm) — these live inside
//     async handlers and try/catch blocks. No boundary will ever see them, so they need the
//     window-level listeners below.
//
// We never reload automatically. A silent reload would discard whatever the user was in the
// middle of (a half-configured wizard is the expensive case), and if the new build failed to
// load too it would become a refresh loop. The user is told, and chooses.

/**
 * Is this error a failed chunk / dynamic-import load?
 *
 * There is no standard for this — every bundler and browser words it differently — so match
 * on the known shapes. Deliberately narrow: a false positive tells the user their app is
 * out of date when it isn't, which is worse than missing one wording.
 */
export const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = String(error.name || "");
  const message = String(error.message || error || "");
  if (name === "ChunkLoadError") return true; // webpack
  return [
    /Loading chunk [\w-]+ failed/i, // webpack
    /Loading CSS chunk [\w-]+ failed/i, // webpack, extracted CSS
    /Failed to fetch dynamically imported module/i, // Chrome / Vite
    /error loading dynamically imported module/i, // Firefox
    /Importing a module script failed/i, // Safari
  ].some((re) => re.test(message));
};

const STALE_BUILD_EVENT = "cdt:stale-build";

/** Announce that a chunk failed to load. Safe to call repeatedly; the notice de-dupes. */
export const notifyStaleBuild = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STALE_BUILD_EVENT));
};

/**
 * Listen for stale-build signals, including the ones that never touch React: an unhandled
 * rejection from an `await import(...)` in an event handler, and the window "error" event.
 * Returns an unsubscribe function.
 */
export const onStaleBuild = (handler) => {
  if (typeof window === "undefined") return () => {};
  const direct = () => handler();
  const fromRejection = (e) => {
    if (isChunkLoadError(e?.reason)) handler();
  };
  const fromError = (e) => {
    if (isChunkLoadError(e?.error || e?.message)) handler();
  };
  window.addEventListener(STALE_BUILD_EVENT, direct);
  window.addEventListener("unhandledrejection", fromRejection);
  window.addEventListener("error", fromError);
  return () => {
    window.removeEventListener(STALE_BUILD_EVENT, direct);
    window.removeEventListener("unhandledrejection", fromRejection);
    window.removeEventListener("error", fromError);
  };
};

/**
 * Wrap a dynamic import so a post-deploy 404 raises the stale-build notice on its way past.
 *
 * Needed because most of these call sites already sit inside try/catch — they render the
 * failure as a panel-local error ("Loading chunk 4025 failed"), which means the rejection is
 * HANDLED and the window listener never fires. The user would be left with a cryptic message
 * and no hint that a reload fixes it. The error is still re-thrown, so each caller's existing
 * error handling is unchanged.
 */
export const importChunk = async (loader) => {
  try {
    return await loader();
  } catch (err) {
    if (isChunkLoadError(err)) notifyStaleBuild();
    throw err;
  }
};

/**
 * Reload onto the current build. `location.reload()` can be served from cache in some
 * browsers, which would land the user on the same dead build; re-requesting the URL with a
 * cache-busting parameter cannot. index.html is uploaded no-cache, so this always resolves
 * to the newest chunk manifest.
 */
export const reloadToLatestBuild = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("_v", String(Date.now()));
  window.location.replace(url.toString());
};
